import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages — Home stays eager (LCP route); everything else is code-split
import Home from "@/pages/Home";
const Models = lazy(() => import("@/pages/Models"));
const ModelDetail = lazy(() => import("@/pages/ModelDetail"));
const BuildYourVan = lazy(() => import("@/pages/BuildYourVan"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Compare = lazy(() => import("@/pages/Compare"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Showroom = lazy(() => import("@/pages/Showroom"));
const ShowroomDetail = lazy(() => import("@/pages/ShowroomDetail"));
const ShowroomCompare = lazy(() => import("@/pages/ShowroomCompare"));
const Testimonials = lazy(() => import("@/pages/Testimonials"));
const AdminVideos = lazy(() => import("@/pages/AdminVideos"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DashboardLogin = lazy(() => import("@/pages/DashboardLogin"));
const DashboardActivate = lazy(() => import("@/pages/DashboardActivate"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
import { SERVICE_PAGES } from "@/data/servicePages";

// Components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LiveChat from "@/components/LiveChat";
import InstantQuoteBot from "@/components/InstantQuoteBot";
import SchemaMarkup from "@/components/SchemaMarkup";
import LiveConsultation from "@/components/LiveConsultation";
import FloatingCallbackCTA from "@/components/FloatingCallbackCTA";
import FloatingContactMenu from "@/components/FloatingContactMenu";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ConsultationProvider } from "@/lib/ConsultationContext";
import { AuthProvider } from "@/lib/AuthContext";
import { initAttribution } from "@/lib/attribution";

function App() {
  useEffect(() => { initAttribution(); }, []);
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
        <ConsultationProvider>
        <ScrollToTop />
        <SchemaMarkup />
        <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          {/* Admin routes — no Header/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
          <Route path="/admin/videos" element={<ProtectedRoute><AdminVideos /></ProtectedRoute>} />

          {/* Owner dashboard — private, noindex, no public chrome */}
          <Route path="/dashboard/login" element={<DashboardLogin />} />
          <Route path="/dashboard/activate" element={<DashboardActivate />} />
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Public routes */}
          <Route path="/*" element={
            <>
              <Header />
              <main className="flex-1">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/models" element={<Models />} />
                  <Route path="/models/:modelId" element={<ModelDetail />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/build" element={<BuildYourVan />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/showroom" element={<Showroom />} />
                  <Route path="/showroom/compare" element={<ShowroomCompare />} />
                  <Route path="/showroom/:modelId" element={<ShowroomDetail />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  {SERVICE_PAGES.map((page) => (
                    <Route key={page.path} path={page.path} element={<ServicePage page={page} />} />
                  ))}
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  {/* Legacy WordPress URLs still indexed by Google */}
                  <Route path="/services" element={<Navigate to="/luxury-van-conversions" replace />} />
                  <Route path="/services/*" element={<Navigate to="/luxury-van-conversions" replace />} />
                  <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
              <InstantQuoteBot />
              <LiveChat />
              <LiveConsultation />
              <FloatingCallbackCTA />
              <FloatingContactMenu
                onOpenChat={() => window.dispatchEvent(new Event('open-chat'))}
                onOpenQuote={() => window.dispatchEvent(new Event('open-quote'))}
                onOpenConsultation={() => window.dispatchEvent(new Event('open-consultation'))}
                onOpenCallback={() => window.dispatchEvent(new Event('open-callback'))}
              />
            </>
          } />
        </Routes>
        </Suspense>
        <Toaster position="top-right" />
        </ConsultationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
